//#region import
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Box,
  Card,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  Tooltip,
  Typography,
} from "@mui/material";
import { TextBoxItem } from "components/Input/TextBox";
import { TextAreaItem } from "components/Input/TextArea";
import DataGrid, { PaginationInterface, initRows } from "components/DataGrid";
import { useLocation, useNavigate } from "react-router";
import { Extension, FnAxios, FnDialog, I18n } from "utilities/utilities";
import SwitchFormItem from "components/Input/Switch";
import { BtnBack, BtnSave } from "components/Button/ButtonAll";
import { SelectItem } from "components/Input/Select/SelectItem";
import UploadFile from "components/Input/UploadFile";
import { GridColDef } from "@mui/x-data-grid-pro";
import { BtnUploadOnTable } from "components/Button/ButtonTable";
import { IDocItem } from "./PublishDocInterface";
import { DatePickerItem } from "components/Input/DatePicker/DatePickerItem";
import moment from "moment";
import ExcelIcon from "../../../../src/assets/images/DocTypeIcon/Excel.svg";
import PDFIcon from "../../../../src/assets/images/DocTypeIcon/PDF.svg";
import PPTIcon from "../../../../src/assets/images/DocTypeIcon/PPT.svg";
import WordIcon from "../../../../src/assets/images/DocTypeIcon/Word.svg";
import DialogPreview from "components/DialogPreview/DialogPreview";
import Avatar from "@mui/material/Avatar";
import { MultipleSelectItem } from "components/Input/Select/MultipleSelectItem";
import ChangeOrder from "components/System/ChangeOrder";
import { GetMenuPermission } from "utilities/SystemFunction";

//#endregion

export default function PublishDocForm() {
  const navigate = useNavigate();
  const AxiosFn = FnAxios();
  const DialogFn = FnDialog();
  const [loadingTable, setLoadingTable] = useState(false);
  const location = useLocation();
  const searchParams = location.search;
  const sMode = new URLSearchParams(searchParams).get("sMode");
  const sID = new URLSearchParams(searchParams).get("sID");

  const form = useForm({
    shouldFocusError: true,
    shouldUnregister: false,
    mode: "all",
  });

  const [nPermission, setPermission] = useState<boolean>(false); // Permission

  const [arrRole, setArrRole] = useState([]);
  const [arrGroup, setArrGroup] = useState([]);
  const [arrUser, setArrUser] = useState([]);

  //Upload Files
  const handleClearAttachFile = useRef(null);
  const handleClearCoverFile = useRef(null);
  const [arrCoverFile, setArrCoverFile] = useState([]);
  const [arrAttachFile, setArrAttachFile] = useState([]);
  const [IsOpenUpload, setIsOpenUpload] = useState(false);
  const [nNoUpload, setNoUpload] = useState(null);

  const [arrRoleSelect, setArrRoleSelect] = useState([]);
  const [arrGroupSelect, setArrGroupSelect] = useState([]);

  const [arrOptionMainGroup, setArrOptionMainGroup] = useState([]);
  const [isPublic, setIsPublic] = useState(true);

  //Table
  const [dataRow, setDataRow] = useState<PaginationInterface>({
    ...initRows,
  });
  const [arrOptionOrder, setArrOptionOrder] = useState([]);
  const dataColumn: GridColDef[] = [
    {
      field: "btnAction",
      headerName: `${I18n.SetText("PublishDoc.sCover")}`,
      align: "center",
      type: "actions",
      resizable: false,
      sortable: false,
      minWidth: 60,
      getActions: (item) => {
        let sUrl = item.row.objCoverFile ? item.row.objCoverFile.sFileLink : "";
        return [
          item.row.objCoverFile ? (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <Avatar
                alt=""
                src={sUrl}
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                onClick={() => handleOpenFile(item.row.objCoverFile)}
              />
            </Tooltip>
          ) : (
            <BtnUploadOnTable
              id={"btnUpload"}
              onClick={() => {
                setIsOpenUpload(true);
                setNoUpload(item.row.nOrder);
              }}
            />
          ),
        ];
      },
    },
    {
      field: "nOrder",
      headerName: `${I18n.SetText("PublishDoc.sOrder")}`,
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: 70,
      renderCell: (item) => (
        <ChangeOrder
          item={item}
          value={item.row.nOrder}
          onChangeOrder={(sID, nOrder) =>
            handleChangeOrder(+nOrder, +item.row.nOrder)
          }
          optOrder={arrOptionOrder || []}
          disabled={!nPermission}
        />
      ),
    },
    {
      field: "sDisplay",
      headerName: `${I18n.SetText("PublishDoc.sShowName")}`,
      headerAlign: "center",
      align: "left",
      sortable: false,
      flex: 1,
      minWidth: 220,
      renderCell: (item) => (
        <TextBoxItem
          id={`sDisplay_${item.row.sID}`}
          name={`sDisplay_${item.row.sID}`}
          label=""
          maxLength={250}
          required={true}
          IsShowMessageError={false}
          type={"text"}
          disabled={!nPermission}
        />
      ),
    },
    {
      field: "sFileName",
      headerName: `${I18n.SetText("PublishDoc.sFileName")}`,
      headerAlign: "center",
      align: "left",
      sortable: false,
      minWidth: 120,
    },
    {
      field: "objFile",
      headerName: `${I18n.SetText("PublishDoc.sFile")}`,
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: 60,
      renderCell: (item) => {
        let sFileType = item.row.sFileName
          .split(".")
          [item.row.sFileName.split(".").length - 1].toLowerCase();

        if (
          sFileType === "jpg" ||
          sFileType === "jpeg" ||
          sFileType === "png"
        ) {
          let sUrl = item.row.objFile.sFileLink;
          return (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <Avatar
                alt=""
                src={sUrl}
                sx={{ width: 40, height: 40, cursor: "pointer" }}
                onClick={() => handleOpenFile(item.row.objFile)}
              />
            </Tooltip>
          );
        } else if (sFileType === "pdf") {
          return (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <img
                style={{ cursor: "pointer" }}
                src={PDFIcon}
                alt=""
                onClick={() => handleOpenFile(item.row.objFile)}
              />
            </Tooltip>
          );
        } else if (sFileType === "doc" || sFileType === "docx") {
          return (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <img
                style={{ cursor: "pointer" }}
                src={WordIcon}
                alt=""
                onClick={() => handleOpenFile(item.row.objFile)}
              />
            </Tooltip>
          );
        } else if (sFileType === "xlsx") {
          return (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <img
                style={{ cursor: "pointer" }}
                src={ExcelIcon}
                alt=""
                onClick={() => handleOpenFile(item.row.objFile)}
              />
            </Tooltip>
          );
        } else if (sFileType === "pptx") {
          return (
            <Tooltip title={`${I18n.SetText("PublishDoc.sShowFile")}`}>
              <img
                style={{ cursor: "pointer" }}
                src={PPTIcon}
                alt=""
                onClick={() => handleOpenFile(item.row.objFile)}
              />
            </Tooltip>
          );
        }
      },
    },
    {
      field: "dDoc",
      headerName: `${I18n.SetText("PublishDoc.sDocDate")}`,
      headerAlign: "center",
      align: "center",
      sortable: false,
      minWidth: 200,
      renderCell: (item) => (
        <DatePickerItem
          id={`dDocDate_${item.row.sID}`}
          name={`dDocDate_${item.row.sID}`}
          fullWidth
          label={""}
          disabled={!nPermission}
          maxDate={moment()}
          required
          IsShowMessageError={false}
        />
      ),
    },
  ];

  const getTable = (p: PaginationInterface) => {
    setLoadingTable(true);
    let objParam = {
      ...p,
    };
    AxiosFn.Post(`PublishDoc/GetTable`, objParam, (result) => {
      setLoadingTable(false);
      setDataRow({
        ...p,
        arrRows: [...result.lstDataRole],
        nDataLength: result.ObjTable?.nDataLength,
        nPageIndex: result.ObjTable?.nPageIndex,
      });
    });
  };

  const handleOpenFile = (e) => {
    let sUrl = e.sFileLink;
    window.open(sUrl, "_blank", "noreferrer");
  };

  const handleDelete = (arrID) => {
    DialogFn.Submit(`${I18n.SetText("PublishDoc.sConfirmDel")}`, () => {
      let arrClone = dataRow;
      arrClone.arrRows = arrClone.arrRows.filter((f) => !arrID.includes(f.sID));
      let arrOption = [];
      //เรียง Order ใหม่
      if (arrClone.arrRows.length > 0) {
        let nOrder = 1;
        arrClone.arrRows.forEach((f) => {
          f.nOrder = nOrder;
          arrOption.push({
            value: nOrder,
            label: nOrder + "",
          });
          nOrder++;
        });
      }

      setDataRow({ ...arrClone });

      setArrOptionOrder([...arrOption]);

      DialogFn.Success("ลบสำเร็จ");
    });
  };

  const handleAddTable = () => {
    let arrClone = dataRow;
    let arrID =
      arrClone.arrRows.length > 0 ? arrClone.arrRows.map((m) => m.sID) : [];
    let nID = (arrID.length > 0 ? Math.max(...arrID) : 0) + 1;
    let arrNewItem = [];
    let arrNewOption = [];

    arrNewItem.push(...arrClone.arrRows);
    //Add New Item
    arrAttachFile.forEach((f) => {
      let arrIDOnItem = arrNewItem.map((m) => m.sID);
      let arrOrderOnItem = arrNewItem.map((m) => m.nOrder);
      let sID = arrNewItem.length > 0 ? Math.max(...arrIDOnItem) + 1 : nID;

      let objNewItem: IDocItem = {};
      objNewItem.sID = sID + "";
      objNewItem.objCoverFile = null;
      objNewItem.nOrder =
        arrNewItem.length > 0 ? Math.max(...arrOrderOnItem) + 1 : nID;
      objNewItem.sDisplay = "";
      objNewItem.sFileName = f.sFileName;
      objNewItem.objFile = f;
      objNewItem.nDocDate = null;

      //Set Option Order
      arrNewOption.push({
        value: objNewItem.nOrder,
        label: objNewItem.nOrder + "",
      });

      //Set Value Form
      form.setValue(`dDocDate_${sID}`, moment());
      form.setValue(`sDisplay_${sID}`, f.sFileName.split(".")[0]);

      arrNewItem.push(objNewItem);
    });

    handleClearAttachFile.current(null);
    setArrAttachFile([]);

    setArrOptionOrder([...arrOptionOrder, ...arrNewOption]);

    arrClone.arrRows = [...arrNewItem];
    setDataRow({ ...arrClone });
  };

  const handleCloseUploadCover = (nNoFile) => {
    if (arrCoverFile.length > 0) {
      let arrClone = dataRow;
      let objClone = arrClone.arrRows.find((f) => f.nOrder === nNoFile);
      objClone.objCoverFile = arrCoverFile[0];

      arrClone.arrRows = [...arrClone.arrRows];
      setDataRow({ ...arrClone });

      handleClearCoverFile.current(null);
    }
    setArrCoverFile([]);
    setIsOpenUpload(false);
  };

  const handleChangeOrder = (nNewOrder, nOldOrder) => {
    let arrClone = dataRow;
    let arrRowClone = arrClone.arrRows;
    let objSelf = arrRowClone.find((f) => f.nOrder === nOldOrder);

    //Order ใหม่มากกว่าเก่า
    if (nNewOrder > nOldOrder) {
      //เคสที่มากกว่าตัวเอง
      let arrChange = arrRowClone.filter(
        (f) => f.nOrder > nOldOrder && f.nOrder <= nNewOrder
      );
      arrChange.forEach((f) => {
        f.nOrder--;
      });
    } else {
      //Order ใหม่น้อยกว่าเก่า
      //เคสที่น้อยกว่าตัวเอง
      let arrChange = arrRowClone.filter(
        (f) => f.nOrder < nOldOrder && f.nOrder >= nNewOrder
      );
      arrChange.forEach((f) => {
        f.nOrder++;
      });
    }

    //อัพเดทตัวเอง
    objSelf.nOrder = nNewOrder;

    arrRowClone = arrRowClone.slice().sort((a, b) => a.nOrder - b.nOrder);

    arrClone.arrRows = [...arrRowClone];
    setDataRow({ ...arrClone });
  };

  const getWarning = () => {
    let sDocGroup =
      sMode === "DocGroup"
        ? `${I18n.SetText("PublishDoc.sDocGroup")}`
        : `${I18n.SetText("PublishDoc.sDocType")}`;
    let sWording = `มี${
      sMode === "DocMain" ? `${I18n.SetText("PublishDoc.sDocMain")}` : sDocGroup
    }นี้ อยู่ในระบบแล้ว!`;
    return sWording;
  };

  const handleSubmit = () => {
    let lstAttach: IDocItem[] = dataRow.arrRows;
    lstAttach.forEach((f) => {
      f.sDisplay = form.getValues(`sDisplay_${f.sID}`);
      f.nDocDate = new Date(form.getValues(`dDocDate_${f.sID}`)).valueOf();
    });

    let nGroupMode = sMode === "DocGroup" ? 2 : 3;
    let objParam = {
      sID: sID,
      nMode: sMode === "DocMain" ? 1 : nGroupMode,
      nParentID: form.getValues("nDocMainGroup"),
      sTitle: form.getValues("sTitle"),
      sDesc: form.getValues("sDesc"),
      lstAttachFile: lstAttach ?? [],
      isPublic: isPublic,
      lstRoleID: form.getValues("lstRole") ?? [],
      lstGroupID: form.getValues("lstGroup") ?? [],
      lstUserID: form.getValues("lstUser") ?? [],
      isActive: form.getValues("isActive"),
      sLocation: location.pathname,
    };

    DialogFn.Submit(`${I18n.SetText("PublishDoc.sConfirmSave")}`, () => {
      DialogFn.BlockUI();
      AxiosFn.Post(
        `PublishDoc/OnSubmit`,
        objParam,
        (result) => {
          DialogFn.UnBlockUI();
          DialogFn.Success(`${I18n.SetText("PublishDoc.sSaveSuccess")}`);
          DialogFn.CloseSubmit();
          handleBack();
        },
        (err) => {
          DialogFn.UnBlockUI();
          if (err.nStatusCode === 409) {
            DialogFn.Warning(getWarning());
          }
        }
      );
    });
  };

  const handleBack = () => {
    navigate("/PublishDocList?sMode=" + sMode);
  };
  const getFormInitial = () => {
    AxiosFn.Get(`PublishDoc/GetFormInitial`, {}, (result) => {
      setArrRole(result.lstRole);

      if (sMode === "DocGroup") {
        setArrOptionMainGroup(result.lstMainDoc);
      } else if (sMode === "DocType") {
        setArrOptionMainGroup(result.lstMainGroupDoc);
      }

      //Defalut Select All
      // if (!sID) {
      //   form.setValue(
      //     "lstRole",
      //     result.lstRole.map((m) => m.value)
      //   );
      //   form.setValue(
      //     "lstGroup",
      //     result.lstGroup.map((m) => m.value)
      //   );
      //   form.setValue(
      //     "lstUser",
      //     result.lstUser.map((m) => m.value)
      //   );

      //   setArrGroup(result.lstGroup);
      //   setArrUser(result.lstUser);
      // }
    });
  };

  useEffect(() => {
    if (arrOptionMainGroup.length > 0) {
      let nMainIndex = arrOptionMainGroup.findIndex(
        (f) => f.value === form.getValues("nDocMainGroup")
      );
      form.setValue(
        "nDocMainGroup",
        nMainIndex !== -1 ? form.getValues("nDocMainGroup") : null
      );
    }
  }, [arrOptionMainGroup]);

  const getFormInfo = () => {
    let objParam = { sID: sID };
    setLoadingTable(true);
    AxiosFn.Post(`PublishDoc/GetFormInfo`, objParam, (result) => {
      //Set Form Value

      form.setValue("nDocMainGroup", result.nParentID);
      form.setValue("sTitle", result.sTitle);
      form.setValue("sDesc", result.sDesc);

      //Attach File
      let arrClone = dataRow;
      arrClone.arrRows = result.lstAttachFile;
      setDataRow(arrClone);

      //Set Form File
      setTimeout(() => {
        let arrOption = [];
        result.lstAttachFile.forEach((iF) => {
          form.setValue(`sDisplay_${iF.sID}`, iF.sDisplay);
          form.setValue(`dDocDate_${iF.sID}`, moment(iF.nDocDate));

          arrOption.push({
            value: iF.nOrder,
            label: iF.nOrder + "",
          });
        });
        setArrOptionOrder([...arrOption]);
      }, 200);

      setIsPublic(result.isPublic);
      form.setValue("lstRole", result.lstRoleID);
      setArrRoleSelect(result.lstRoleID);
      if (result.lstRoleID.length > 0) {
        handleChangeRole(result.lstRoleID);
        form.setValue("lstGroup", result.lstGroupID);
        setArrGroupSelect(result.lstGroupID);
      }
      if (result.lstGroupID.length > 0) {
        handleChangeGroup(result.lstGroupID);
        form.setValue("lstUser", result.lstUserID);
      }
      form.setValue("isActive", result.isActive);

      setLoadingTable(false);
    });
  };

  const handleChangeRole = (e) => {
    if (e && e.length > 0) {
      let objParam = { lstID: e };
      DialogFn.BlockUI();
      AxiosFn.Post(`PublishDoc/GetGroup`, objParam, (result) => {
        DialogFn.UnBlockUI();
        setArrGroup(result.lstGroup);
      });
    } else {
      setArrGroup([]);
      setArrUser([]);
    }

    if (e.length < arrRoleSelect.length) {
      form.setValue("lstGroup", null);
      form.setValue("lstUser", null);
    }
    setArrRoleSelect(e);
  };

  const handleChangeGroup = (e) => {
    if (e && e.length > 0) {
      let objParam = { lstID: e };
      DialogFn.BlockUI();
      AxiosFn.Post(`PublishDoc/GetUser`, objParam, (result) => {
        DialogFn.UnBlockUI();
        setArrUser(result.lstUser);
      });
    } else {
      setArrUser([]);
    }

    if (e.length < arrGroupSelect.length) {
      form.setValue("lstUser", null);
    }
    setArrGroupSelect(e);
  };

  useEffect(() => {
    if (arrAttachFile.length > 0) {
      handleAddTable();
    }
  }, [arrAttachFile]);

  useEffect(() => {
    if (arrCoverFile.length > 0) {
      handleCloseUploadCover(nNoUpload);
    }
  }, [arrCoverFile]);

  useEffect(() => {
    GetMenuPermission(AxiosFn, setPermission, DialogFn);

    getFormInitial();
    if (!sID) {
      form.setValue("isActive", true);
    } else {
      getFormInfo();
    }
  }, []);

  const getTitle = () => {
    let sText = "";
    switch (sMode) {
      case "DocMain":
        sText = `${I18n.SetText("PublishDoc.sDocMain")}`;
        break;
      case "DocGroup":
        sText = `${I18n.SetText("PublishDoc.sDocGroup")}`;
        break;
      case "DocType":
        sText = `${I18n.SetText("PublishDoc.sDocType")}`;
        break;
    }
    return sText;
  };

  return (
    <FormProvider {...form}>
      <Grid container spacing={1}>
        {(sMode === "DocGroup" || sMode === "DocType") && (
          <Grid item xs={12}>
            <SelectItem
              id={"selDocMain"}
              name={"nDocMainGroup"}
              label={
                sMode === "DocGroup"
                  ? `${I18n.SetText("PublishDoc.sDocMain")}`
                  : `${I18n.SetText("PublishDoc.sDocMainGroup")}`
              }
              options={arrOptionMainGroup || []}
              disabled={!nPermission}
              required={true}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <TextBoxItem
            id={"txtDocName"}
            name="sTitle"
            label={getTitle()}
            maxLength={150}
            required={true}
            type={"text"}
            disabled={!nPermission}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <TextAreaItem
            id={"txtDesc"}
            name="sDesc"
            label={`${I18n.SetText("PublishDoc.sDocDesc")}`}
            maxLength={1000}
            maxRows={3}
            required={false}
            disabled={!nPermission}
          />
        </Grid>
        <Grid item xs={12} md={12}>
          <Typography fontWeight={"bold"}>{`${I18n.SetText(
            "PublishDoc.sAttachFile"
          )}`}</Typography>
          <Grid xs={12} mb={1}>
            <Divider />
          </Grid>
          <UploadFile
            id="upAttach"
            name={"arrUploadFileAttach"}
            label={""}
            required={false}
            arrFile={arrAttachFile}
            setarrFile={setArrAttachFile}
            IsFolder={true}
            IsMultiple={true}
            Extension={[
              "jpg",
              "jpeg",
              "png",
              "pdf",
              "doc",
              "docx",
              "xlsx",
              "pptx",
            ]}
            nLimitFile={30}
            sLimitFile={"MB"}
            onClearFile={handleClearAttachFile}
            sPositionText="right"
            IsCrop={false}
            cropMovable={false}
            cropResize={false}
            cropShape="retangle"
            modeDisplay="list"
            disabled={!nPermission}
          />
        </Grid>

        <Grid item xs={12} sx={{ marginTop: "2rem" }}>
          <DataGrid
            sTableID={`tbPublishForm_${sMode}`}
            columns={dataColumn}
            rows={dataRow}
            onLoadData={getTable}
            onDelete={handleDelete}
            isLoading={loadingTable}
            isHiddenToolHead
            isHiddenPagination
            isShowCheckBox={nPermission}
          />
        </Grid>

        <Grid item xs={12} md={12} mt={2}>
          <Typography fontWeight={"bold"}>
            กำหนดสิทธิ์การเผยแพร่ข้อมูล{" "}
            <span style={{ color: "red", fontWeight: "normal" }}> *</span>
          </Typography>
          <Grid xs={12} mb={1}>
            <Divider />
          </Grid>
          <FormControlLabel
            disabled={!nPermission}
            checked={isPublic}
            control={<Radio />}
            label={`${I18n.SetText("PublishDoc.sPublic")}`}
            onChange={() => setIsPublic(true)}
          />
          <FormControlLabel
            disabled={!nPermission}
            checked={!isPublic}
            control={<Radio />}
            label={`${I18n.SetText("PublishDoc.sPermOnly")}`}
            onChange={() => setIsPublic(false)}
          />
        </Grid>
        {!isPublic && (
          <Grid item xs={12}>
            <Card sx={{ padding: 3 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <MultipleSelectItem
                    id={"selRole"}
                    name={"lstRole"}
                    label={`${I18n.SetText("PublishDoc.sRole")}`}
                    options={arrRole || []}
                    onChange={(e) => handleChangeRole(e)}
                    disabled={!nPermission}
                    required={true}
                    limitTag={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultipleSelectItem
                    id={"selGroup"}
                    name={"lstGroup"}
                    label={`${I18n.SetText("PublishDoc.sUserGroup")}`}
                    options={arrGroup || []}
                    onChange={(e) => handleChangeGroup(e)}
                    disabled={!nPermission}
                    required={false}
                    limitTag={4}
                  />
                </Grid>
                <Grid item xs={12}>
                  <MultipleSelectItem
                    id={"selUser"}
                    name={"lstUser"}
                    label={`${I18n.SetText("PublishDoc.sUser")}`}
                    options={arrUser || []}
                    disabled={!nPermission}
                    required={false}
                  />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={12} mt={1}>
          <Typography fontWeight={"bold"}>
            {`${I18n.SetText("PublishDoc.sStatus")}`}
            <span style={{ color: "red", fontWeight: "normal" }}> *</span>
          </Typography>
          <Grid xs={12} mb={1}>
            <Divider />
          </Grid>
          <Grid item xs={12}>
            <SwitchFormItem
              id={"isActive"}
              name={"isActive"}
              checkText={`${I18n.SetText("PublishDoc.sActive")}`}
              uncheckText={`${I18n.SetText("PublishDoc.sInactive")}`}
              disabled={!nPermission}
              width={130}
            />
          </Grid>
        </Grid>

        {/* Action */}
        <Grid item xs={12}>
          <Grid container justifyContent="flex-start" sx={{ mt: 5 }}>
            <Grid item xs={6} md={6}>
              <BtnBack id={"btnBack"} onClick={() => handleBack()} />
            </Grid>

            {nPermission ? (
              <>
                <Grid container item xs={6} md={6} justifyContent="flex-end">
                  <BtnSave
                    id={"btnSave"}
                    onClick={form.handleSubmit(
                      () => handleSubmit(),
                      (err) => console.log("err", err)
                    )}
                  />
                </Grid>
              </>
            ) : (
              <></>
            )}
          </Grid>
        </Grid>

        {/* Dialog Upload files */}
        <DialogPreview
          IsOpen={IsOpenUpload}
          Title={`${I18n.SetText("PublishDoc.sUploadCover")}`}
          required={true}
          onClose={() => {
            setIsOpenUpload(false);
            setArrCoverFile([]);
          }}
          sMaxWidth="sm"
          CloseSave={true}
        >
          <Box sx={{ marginLeft: 1 }}>
            <UploadFile
              id={`arrCoverFile_${nNoUpload}`}
              name={`arrCoverFile_${nNoUpload}`}
              IsDrag={false}
              required={true}
              arrFile={arrCoverFile}
              setarrFile={setArrCoverFile}
              IsFolder={false}
              IsMultiple={false}
              Extension={[...Extension.Image]}
              nLimitFile={10}
              sLimitFile={"MB"}
              onClearFile={handleClearCoverFile}
              modeDisplay={"list"}
              sFolderTemp={""}
              IsMultiDelete={false}
              sPositionText={"right"}
              disabled={!nPermission}
            />
          </Box>

          <Box sx={{ height: "40px" }}></Box>
        </DialogPreview>
      </Grid>
    </FormProvider>
  );
}
